/**
 * DeliveryChannelSetting Component (AC-1816, epic AC-1813)
 *
 * Per-workflow "Deliver results to" setting for the WorkflowEditor settings row.
 * Mirrors AutoSummarizeToggle / WhatsAppNotificationToggle: a controlled
 * component whose current values come from the workflow object, and which
 * persists changes via `apiService.updateWorkflow(id, { delivery_*,
 * skip_version_control: true })` -- the exact call AutoSummarizeToggle uses for
 * `auto_summarize`.
 *
 * Product model (locked in the technical design):
 *   - A workflow has an OPTIONAL delivery setting: a channel (None / WhatsApp /
 *     Slack / Email) plus a destination (Slack channel like `#general`, or an
 *     email address; WhatsApp needs no destination -> the connected number).
 *   - Only channels whose built-in is CONNECTED are offered. Unavailable
 *     channels are disabled and guide the user to set them up in Settings.
 *   - For channels that expose multiple connected instances (Slack/Email), an
 *     optional credential/instance selector chooses which one to send through.
 *
 * The three persisted fields mirror the backend columns on `ai.orchestration`:
 *   delivery_channel, delivery_destination, delivery_credential_id.
 */

import React, { useState } from 'react';
import { Send, Loader } from 'lucide-react';
import apiService from '../../services/api';
import type { ChannelAvailability, DeliveryChannel } from '../../types';

interface DeliveryChannelSettingProps {
  orchestrationId: number;
  /** Currently selected channel, or null for no delivery. */
  channel: DeliveryChannel | null;
  /** Currently selected destination (Slack channel / email address), or null. */
  destination: string | null;
  /** Currently selected built-in credential/instance id, or null for default. */
  credentialId: number | null;
  /** Per-channel availability (which built-ins are connected). */
  availableChannels: ChannelAvailability[];
  /**
   * Called after a successful persist with the next setting. Lets the parent
   * (WorkflowEditor) keep the workflow object in sync without a refetch.
   */
  onChange: (next: {
    channel: DeliveryChannel | null;
    destination: string | null;
    credentialId: number | null;
  }) => void;
}

/** Display order + labels for the channel select. */
const CHANNEL_LABELS: Array<{ value: DeliveryChannel; label: string }> = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'slack', label: 'Slack' },
  { value: 'email', label: 'Email' },
];

/** Channels that require a destination value before saving. */
const DESTINATION_REQUIRED: Record<DeliveryChannel, boolean> = {
  whatsapp: false, // delivered to the connected number; no destination needed
  slack: true,
  email: true,
};

function destinationPlaceholder(channel: DeliveryChannel): string {
  switch (channel) {
    case 'slack':
      return '#general';
    case 'email':
      return 'you@example.com';
    default:
      return '';
  }
}

function destinationLabel(channel: DeliveryChannel): string {
  switch (channel) {
    case 'slack':
      return 'Slack channel';
    case 'email':
      return 'Email address';
    default:
      return 'Destination';
  }
}

const DeliveryChannelSetting: React.FC<DeliveryChannelSettingProps> = ({
  orchestrationId,
  channel,
  destination,
  credentialId,
  availableChannels,
  onChange,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Local draft for the destination input so typing doesn't fire a save per
  // keystroke; persisted on blur / channel-select (see persist()).
  const [destinationDraft, setDestinationDraft] = useState<string>(
    destination ?? ''
  );

  const availabilityFor = (
    c: DeliveryChannel
  ): ChannelAvailability | undefined =>
    availableChannels.find((a) => a.channel === c);

  const isAvailable = (c: DeliveryChannel): boolean =>
    availabilityFor(c)?.available === true;

  const currentAvailability = channel ? availabilityFor(channel) : undefined;
  const credentialOptions = currentAvailability?.credentials ?? [];

  const persist = async (next: {
    channel: DeliveryChannel | null;
    destination: string | null;
    credentialId: number | null;
  }) => {
    try {
      setIsSaving(true);
      setError(null);
      await apiService.updateWorkflow(String(orchestrationId), {
        delivery_channel: next.channel,
        delivery_destination: next.destination,
        delivery_credential_id: next.credentialId,
        skip_version_control: true,
      });
      onChange(next);
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('[DeliveryChannelSetting] Save failed:', err);
      setError('Failed to update');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChannelChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const raw = e.target.value;
    if (raw === '') {
      // "None" -- clear the whole setting.
      setDestinationDraft('');
      void persist({ channel: null, destination: null, credentialId: null });
      return;
    }
    const nextChannel = raw as DeliveryChannel;
    // Selecting an unavailable channel is prevented by the disabled options, but
    // guard anyway.
    if (!isAvailable(nextChannel)) return;

    if (DESTINATION_REQUIRED[nextChannel]) {
      // Wait for a destination before persisting; just reflect the channel in
      // the UI by clearing the draft and letting the input render.
      setDestinationDraft('');
      void persist({
        channel: nextChannel,
        destination: null,
        credentialId: null,
      });
    } else {
      // WhatsApp: no destination needed -- persist immediately.
      setDestinationDraft('');
      void persist({
        channel: nextChannel,
        destination: null,
        credentialId: null,
      });
    }
  };

  const handleDestinationBlur = () => {
    if (!channel) return;
    const trimmed = destinationDraft.trim();
    const nextDestination = trimmed === '' ? null : trimmed;
    if ((destination ?? '') === (nextDestination ?? '')) return; // no change
    void persist({ channel, destination: nextDestination, credentialId });
  };

  const handleCredentialChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    if (!channel) return;
    const raw = e.target.value;
    const nextId = raw === '' ? null : Number(raw);
    const effectiveDestination =
      destination ?? (destinationDraft.trim() === '' ? null : destinationDraft.trim());
    void persist({
      channel,
      destination: effectiveDestination,
      credentialId: nextId,
    });
  };

  const unavailableHint = channel
    ? undefined
    : availableChannels
        .filter((a) => !a.available && a.reason)
        .map((a) => a.reason)
        .find(Boolean);

  return (
    <div className="flex flex-col py-2" data-testid="delivery-channel-setting">
      <div className="flex items-center min-w-0 mb-1">
        <Send className="w-4 h-4 text-blue-600 dark:text-blue-500 mr-2 flex-shrink-0" />
        <label
          htmlFor={`delivery-channel-${orchestrationId}`}
          className="text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          Deliver results to
        </label>
        {isSaving && (
          <Loader className="w-3 h-3 ml-2 text-gray-400 animate-spin" />
        )}
      </div>

      <select
        id={`delivery-channel-${orchestrationId}`}
        aria-label="Deliver results to"
        value={channel ?? ''}
        disabled={isSaving}
        onChange={handleChannelChange}
        className="text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <option value="">None</option>
        {CHANNEL_LABELS.map(({ value, label }) => {
          const avail = isAvailable(value);
          const reason = availabilityFor(value)?.reason;
          return (
            <option key={value} value={value} disabled={!avail}>
              {avail ? label : `${label} (${reason ?? 'set up in Settings'})`}
            </option>
          );
        })}
      </select>

      {/* Destination input -- only for channels that need one (Slack / Email). */}
      {channel && DESTINATION_REQUIRED[channel] && (
        <div className="mt-2">
          <label
            htmlFor={`delivery-destination-${orchestrationId}`}
            className="block text-xs text-gray-500 dark:text-gray-400 mb-1"
          >
            {destinationLabel(channel)}
          </label>
          <input
            id={`delivery-destination-${orchestrationId}`}
            type="text"
            aria-label={destinationLabel(channel)}
            value={destinationDraft}
            placeholder={destinationPlaceholder(channel)}
            disabled={isSaving}
            onChange={(e) => setDestinationDraft(e.target.value)}
            onBlur={handleDestinationBlur}
            className="w-full text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          />
        </div>
      )}

      {/* WhatsApp: no destination -- it goes to the connected number. */}
      {channel === 'whatsapp' && (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Sent to your connected WhatsApp number.
        </p>
      )}

      {/* Instance / credential selector -- only when the channel exposes more
          than one connected built-in instance. */}
      {channel && credentialOptions.length > 1 && (
        <div className="mt-2">
          <label
            htmlFor={`delivery-credential-${orchestrationId}`}
            className="block text-xs text-gray-500 dark:text-gray-400 mb-1"
          >
            Account
          </label>
          <select
            id={`delivery-credential-${orchestrationId}`}
            aria-label="Delivery account"
            value={credentialId ?? ''}
            disabled={isSaving}
            onChange={handleCredentialChange}
            className="w-full text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <option value="">Default</option>
            {credentialOptions.map((c) => (
              <option key={c.credential_id} value={c.credential_id}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Setup guidance when no channel is selectable / chosen. */}
      {!channel && unavailableHint && (
        <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
          {unavailableHint}. Connect it in Settings to deliver results.
        </p>
      )}

      {error && (
        <p className="mt-1 text-xs text-red-500 dark:text-red-400">{error}</p>
      )}
    </div>
  );
};

export default DeliveryChannelSetting;
